export interface ClassEvent {
  id: string;
  productname: string;
  start: string; // "2026-02-10 16:00:00"
  end: string;
  employee_names: { service_provider_name: string }[];
  customer: string[];
  maxparticipants: number;
  colorcode?: string | null;
}

export type ClassCategory =
  | "lapset"
  | "nuoret"
  | "aikuiset"
  | "edistyneet"
  | "alkeet"
  | "vapaavuoro"
  | "synttarit"
  | "yksityisohjaus"
  | "tapahtuma";

export const CATEGORY_LABELS: Record<ClassCategory, string> = {
  lapset: "Lapset",
  nuoret: "Nuoret",
  aikuiset: "Aikuiset",
  edistyneet: "Edistyneet",
  alkeet: "Alkeet",
  vapaavuoro: "Vapaavuoro",
  synttarit: "Synttärit",
  yksityisohjaus: "Yksityis",
  tapahtuma: "Tapahtuma",
};

// Strip zero-width Unicode chars
function clean(s: string): string {
  return s.replace(/[\u200B-\u200F\u2028-\u202F\uFEFF]/g, "").trim();
}

export function classifyClass(productname: string): ClassCategory {
  const name = clean(productname).toLowerCase();

  // Phase 1: special categories
  if (name.includes("vapaavuoro")) return "vapaavuoro";
  if (name.includes("synttäri")) return "synttarit";
  if (name.includes("yksityisohjaus")) return "yksityisohjaus";

  // Phase 2: age detection
  // "Taapero", "Pikkuapinat", "Perhe-" prefix → lapset
  if (name.includes("taapero") || name.includes("pikkuapinat") || name.startsWith("perhe-") || name.includes("perheiden")) return "lapset";

  // "Nuoret/Nuorten" keyword
  if (/\bnuort(?:en|et)\b/.test(name)) return "nuoret";

  // Parse "X-Yv" age ranges
  const ageRange = name.match(/(\d+)\s*[-–]\s*(\d+)\s*v/);
  if (ageRange) {
    const low = parseInt(ageRange[1]!, 10);
    const high = parseInt(ageRange[2]!, 10);
    if (high <= 12) return "lapset";
    if (low >= 13) return "nuoret";
    // Mixed range crossing 13
    return "nuoret";
  }

  // Single age "Xv" or "X v"
  const singleAge = name.match(/(\d+)\s*v\b/);
  if (singleAge) {
    const age = parseInt(singleAge[1]!, 10);
    if (age <= 12) return "lapset";
  }

  // "yli Xv" — ≥13 → aikuiset
  const yliAge = name.match(/yli\s+(\d+)\s*v/);
  if (yliAge) {
    const age = parseInt(yliAge[1]!, 10);
    if (age >= 13) return "aikuiset";
  }

  // Phase 3: "Aikuiset/Aikuisten" keyword
  if (/\baikuist(?:en|et)\b/.test(name)) return "aikuiset";

  // Phase 4: skill keywords
  if (/\bedistyne/.test(name) || /\bjatko\b/.test(name) || /\baktiivi\b/.test(name)) return "edistyneet";
  if (/\balkee/.test(name) || /\bperustee/.test(name)) return "alkeet";

  // Phase 5: specific adult products
  if (
    name.includes("parkourfit") ||
    name.includes("käsinseisonta") ||
    name.includes("akrojooga") ||
    name.includes("liikkuvuus") ||
    name.includes("tolppa")
  ) return "aikuiset";

  // Phase 6: fallback
  return "tapahtuma";
}
